import { ChevronRight, MoreVert } from "@mui/icons-material";
import {
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
} from "@mui/material";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import GenericButton from "src/components/GenericButton/GenericButton";

const SUBMENU_OFFSET = [-10, 10];
const TRAILING_ICON_GAP = 21;

export type MoreOptionsButton = {
  title: string;
  tooltipTitle?: string;
  endIcon?: ReactNode;
  onClick?: () => void | Promise<void>;
  onSubMenuOpen?: () => void | Promise<void>;
  subButtons?: MoreOptionsButton[];
  disabled?: boolean;
  icon?: ReactNode;
};

const getTrailingIcon = (button: MoreOptionsButton) => {
  if (button.subButtons?.length) {
    return <ChevronRight fontSize="small" />;
  }

  return button.endIcon ?? null;
};

const MoreOptionsMenuItemContent = ({
  button,
}: {
  button: MoreOptionsButton;
}) => {
  const trailingIcon = getTrailingIcon(button);

  return (
    <>
      {button.icon && (
        <ListItemIcon sx={{ minWidth: 32 }}>{button.icon}</ListItemIcon>
      )}
      {button.title}
      {trailingIcon && (
        <ListItemIcon
          sx={{
            alignItems: "center",
            justifyContent: "flex-end",
            marginLeft: "auto",
            minWidth: 24,
            paddingLeft: `${TRAILING_ICON_GAP}px`,
          }}
        >
          {trailingIcon}
        </ListItemIcon>
      )}
    </>
  );
};

export type MoreOptionsGenericProps = {
  normalButtons?: MoreOptionsButton[];
  dangerZoneButtons?: MoreOptionsButton[];
  isDisabled?: boolean;
  dataTestId?: string;
  menuAnchorPosition?: { top: number; left: number } | null;
  triggerIcon?: ReactNode;
  triggerText?: string;
  triggerTooltipTitle?: ReactNode;
  hideTriggerTooltip?: boolean;
  renderTrigger?: boolean;
  triggerAriaLabel?: string;
  menuId?: string;
  onMenuClose?: () => void;
};

export const DocViewerMoreOptions = ({
  normalButtons = [],
  dangerZoneButtons = [],
  isDisabled = false,
  dataTestId = "doc-viewer-more-options-button",
  menuAnchorPosition = null,
  triggerIcon,
  triggerText,
  triggerTooltipTitle,
  hideTriggerTooltip = true,
  renderTrigger = true,
  triggerAriaLabel,
  menuId = "more-options-menu",
  onMenuClose,
}: MoreOptionsGenericProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [activeSubMenuButtonTitle, setActiveSubMenuButtonTitle] = useState<
    string | null
  >(null);
  const subMenuListRef = useRef<HTMLUListElement | null>(null);
  const open =
    !isDisabled && (Boolean(anchorEl) || Boolean(menuAnchorPosition));
  const subMenuOpen = Boolean(subMenuAnchorEl);
  const activeSubMenuButtons = useMemo(() => {
    const activeSubMenuButton = [...normalButtons, ...dangerZoneButtons].find(
      (button) => button.title === activeSubMenuButtonTitle
    );

    return activeSubMenuButton?.subButtons ?? [];
  }, [activeSubMenuButtonTitle, dangerZoneButtons, normalButtons]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleButtonClick = (onClick: () => void | Promise<void>) => {
    handleClose();
    void onClick();
  };

  const handleCloseSubMenu = () => {
    setSubMenuAnchorEl(null);
    setActiveSubMenuButtonTitle(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
    handleCloseSubMenu();
    onMenuClose?.();
  };

  const handleSubMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    button: MoreOptionsButton
  ) => {
    event.stopPropagation();
    setSubMenuAnchorEl(event.currentTarget);
    setActiveSubMenuButtonTitle(button.title);
    void button.onSubMenuOpen?.();
  };

  const handleSubMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Escape") return;

    const trigger = anchorEl;
    event.preventDefault();
    event.stopPropagation();
    handleClose();
    window.setTimeout(() => trigger?.focus(), 0);
  };

  const hasDangerButtons = dangerZoneButtons.length > 0;

  useEffect(() => {
    if (!subMenuOpen) return;

    const focusTimer = window.setTimeout(() => {
      const firstEnabledItem =
        subMenuListRef.current?.querySelector<HTMLElement>(
          '[role="menuitem"]:not([aria-disabled="true"])'
        );
      firstEnabledItem?.focus();
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [activeSubMenuButtons, subMenuOpen]);

  return (
    <div>
      {renderTrigger && (
        <GenericButton
          dataTestId={dataTestId}
          isDisabled={isDisabled}
          ariacontrols={open ? menuId : undefined}
          ariahaspopup={true}
          ariaexpanded={open ? true : undefined}
          onClick={handleClick}
          text={triggerText}
          tooltipContent={triggerTooltipTitle}
          hideTooltip={hideTriggerTooltip}
          ariaLabel={triggerAriaLabel}
        >
          {triggerIcon ?? <MoreVert />}
        </GenericButton>
      )}
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        anchorReference={menuAnchorPosition ? "anchorPosition" : "anchorEl"}
        anchorPosition={menuAnchorPosition ?? undefined}
        open={open}
        onClose={handleClose}
        // The submenu is a Popper portal, so allow focus to move into it while open.
        disableEnforceFocus={subMenuOpen}
        MenuListProps={{
          "aria-label": renderTrigger ? undefined : triggerAriaLabel,
          "aria-labelledby": renderTrigger ? dataTestId : undefined,
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        slotProps={{
          root: {
            sx: {
              pointerEvents: open ? "auto" : "none",
            },
          },
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {normalButtons.map((button) => (
          <Tooltip key={button.title} title={button.tooltipTitle ?? ""}>
            <span style={{ display: "block" }}>
              <MenuItem
                onClick={(event) => {
                  if (button.subButtons?.length) {
                    handleSubMenuOpen(event, button);
                    return;
                  }
                  if (button.onClick) {
                    handleButtonClick(button.onClick);
                  }
                }}
                disabled={button.disabled}
              >
                <MoreOptionsMenuItemContent button={button} />
              </MenuItem>
            </span>
          </Tooltip>
        ))}

        {hasDangerButtons && (
          <>
            <ListSubheader sx={{ color: "error.main" }}>
              Danger Zone
            </ListSubheader>

            {dangerZoneButtons.map((button) => (
              <Tooltip key={button.title} title={button.tooltipTitle ?? ""}>
                <span style={{ display: "block" }}>
                  <MenuItem
                    onClick={() => {
                      if (button.onClick) {
                        handleButtonClick(button.onClick);
                      }
                    }}
                    disabled={button.disabled}
                  >
                    <MoreOptionsMenuItemContent button={button} />
                  </MenuItem>
                </span>
              </Tooltip>
            ))}
          </>
        )}
      </Menu>
      <Popper
        id={`${menuId}-submenu`}
        anchorEl={subMenuAnchorEl}
        open={subMenuOpen}
        placement="right-start"
        modifiers={[
          {
            name: "offset",
            options: {
              offset: SUBMENU_OFFSET,
            },
          },
        ]}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <Paper elevation={8}>
          <MenuList
            aria-labelledby={menuId}
            onKeyDown={handleSubMenuKeyDown}
            ref={subMenuListRef}
          >
            {activeSubMenuButtons.map((button) => (
              <Tooltip key={button.title} title={button.tooltipTitle ?? ""}>
                <span style={{ display: "block" }}>
                  <MenuItem
                    onClick={() => {
                      if (button.onClick) {
                        handleButtonClick(button.onClick);
                      }
                    }}
                    disabled={button.disabled}
                  >
                    <MoreOptionsMenuItemContent button={button} />
                  </MenuItem>
                </span>
              </Tooltip>
            ))}
          </MenuList>
        </Paper>
      </Popper>
    </div>
  );
};

export default DocViewerMoreOptions;
